import { useEffect, useState, useCallback, type ReactNode } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

/**
 * M12 §29 #5 — Cost Analysis (dedicated screen).
 *
 * A focused cost & variance breakdown for a single review, sourced from
 * `/v1/sh/review/:id/cost-validation` (the same server-classified variance the
 * review console shows inline). §11/§12 — variance % and tier are computed
 * server-side; this screen only surfaces the classified result (§14 no
 * client-side threshold hardcoding).
 */
interface CostValidation {
  readonly validationId: string;
  readonly estimatedCostInr: number;
  readonly approvedCostInr: number;
  readonly actualCostInr: number;
  readonly varianceAmountInr: number;
  readonly variancePct: number;
  readonly varianceTierCode: string;
  readonly validatedAt: string;
}

interface ReviewSummary {
  readonly reviewId: string;
  readonly jobNumber: string | null;
  readonly status: string;
  readonly isHighValue: boolean;
  readonly varianceTierCode: string | null;
}

const CARD: React.CSSProperties = {
  padding: 16, borderRadius: 8, marginBottom: 16,
  border: '1px solid var(--color-border-default)',
  background: 'var(--color-bg-elevated)',
};
const LABEL: React.CSSProperties = { fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 2 };
const VAL: React.CSSProperties = { fontSize: 14, fontWeight: 500, marginBottom: 12 };


export function ShCostAnalysisPage(): ReactNode {
  const { reviewId } = useParams<{ reviewId: string }>();
  const { token } = useAuth();
  const { t } = useTranslation();
  // Amounts follow the tenant's DISPLAY_NUMBER_FORMAT, not a locale in the source.
  const { money, number } = useBusinessNumber();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const [cost, setCost] = useState<CostValidation | null>(null);
  const [review, setReview] = useState<ReviewSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!reviewId) return;
    setLoading(true);
    try {
      // The validation answers null when none exists, so a rejection is a
      // failure and is said. The review adds the job number and the high-value cell.
      const [cv, rv] = await Promise.all([
        apiRequest<CostValidation | null>(`/v1/sh/review/${encodeURIComponent(reviewId)}/cost-validation`, { token }),
        apiRequest<ReviewSummary>(`/v1/sh/review/${encodeURIComponent(reviewId)}`, { token }).catch(() => null),
      ]);
      setCost(cv);
      setReview(rv);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [reviewId, token]);

  useEffect(() => { void load(); }, [load]);

  return (
    <main aria-label="sh-cost-analysis">
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
        <Link to={`/sh/review/${reviewId}`} data-testid="back-review">{t('sh.common.review')}</Link>
        <h1 style={{ fontSize: 20 }}>{t('sh.cost.title', { job: review?.jobNumber ?? '—' })}</h1>
      </div>

      {error && <p role="alert" style={{ color: 'var(--color-danger)', marginBottom: 12 }}>{error}</p>}

      {loading ? (
        <p>{t('sh.common.loading')}</p>
      ) : error ? null : !cost ? (
        <p style={{ color: 'var(--color-text-muted)' }} data-testid="cost-empty">
          {t('sh.cost.empty')}
        </p>
      ) : (
        <>
          <section style={CARD} aria-label="cost-breakdown">
            <h2 style={{ fontSize: 16, marginBottom: 12 }}>{t('sh.cost.breakdown')}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
              <div><div style={LABEL}>{t('sh.cost.estimatedCost')}</div><div style={VAL}>₹{money(cost.estimatedCostInr)}</div></div>
              <div><div style={LABEL}>{t('sh.cost.approvedCost')}</div><div style={VAL}>₹{money(cost.approvedCostInr)}</div></div>
              <div><div style={LABEL}>{t('sh.cost.actualCost')}</div><div style={VAL}>₹{money(cost.actualCostInr)}</div></div>
              <div><div style={LABEL}>{t('sh.cost.validated')}</div><div style={VAL}>{at(cost.validatedAt)}</div></div>
            </div>
          </section>

          <section style={CARD} aria-label="variance-analysis">
            <h2 style={{ fontSize: 16, marginBottom: 12 }}>{t('sh.cost.varianceAnalysis')}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
              <div><div style={LABEL}>{t('sh.cost.varianceAmount')}</div><div style={VAL}>₹{money(cost.varianceAmountInr)}</div></div>
              <div>
                <div style={LABEL}>{t('sh.cost.variancePct')}</div>
                <div style={{ ...VAL, color: cost.variancePct > 0 ? 'var(--color-danger)' : 'var(--color-success)' }} data-testid="variance-pct">
                  {number(cost.variancePct)}%
                </div>
              </div>
              <div><div style={LABEL}>{t('sh.cost.approvalTier')}</div><div style={VAL} data-testid="variance-tier">{cost.varianceTierCode}</div></div>
              {review && <div><div style={LABEL}>{t('sh.cost.highValue')}</div><div style={VAL}>{review.isHighValue ? t('sh.common.yes') : t('sh.common.no')}</div></div>}
            </div>
            <p style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
              {t('sh.cost.formula')}
            </p>
          </section>
        </>
      )}
    </main>
  );
}
